// Scanner-session defaults only. Never edits collection rows or recognition scope.
export const BATCH_FINISHES = [
  { value: 'Auto', label: 'Auto' }, { value: 'Normal', label: 'Normal' },
  { value: 'Reverse Holofoil', label: 'Reverse Holo' }, { value: 'Holofoil', label: 'Holo' },
];
const fields = { Normal: 'finish_normal', Holofoil: 'finish_holofoil', 'Reverse Holofoil': 'finish_reverse_holofoil' };
export function finishSupport(card, printing) {
  if (['mtg', 'lorcana'].includes(card?.game) && printing === 'Reverse Holofoil') return 'unsupported';
  const value = card?.[fields[printing]];
  return value == null ? 'unknown' : Number(value) === 0 ? 'unsupported' : 'supported';
}
const words = ['zero','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen','twenty'];
export function parseBatchFinish(text) {
  const s = String(text || '').toLowerCase().trim().replace(/[.!?]+$/g, '').replace(/-/g, ' ').replace(/\s+/g, ' ');
  if (/^(?:back to|reset to|switch to) auto$/.test(s)) return { mode: 'Auto', count: null };
  const finish = '(normal|reverse holo(?:foil)?|holo(?:foil)?|auto)';
  const next = s.match(new RegExp('^(?:the )?next ([a-z]+|[0-9]+)(?: cards?)? ' + finish + '$'));
  const until = s.match(new RegExp('^(?:until i change(?: it)?|from now on) ' + finish + '$'));
  if (!next && !until) return null; // bare "holo" remains an existing-card correction
  const raw = next ? next[2] : until[1];
  const mode = raw === 'normal' ? 'Normal' : raw.startsWith('reverse') ? 'Reverse Holofoil' : raw.startsWith('holo') ? 'Holofoil' : 'Auto';
  const count = next ? (/^\d+$/.test(next[1]) ? Number(next[1]) : words.indexOf(next[1])) : null;
  if (count !== null && (!Number.isInteger(count) || count < 1 || count > 999)) return { error: 'Choose 1–999 successful copies.' };
  return { mode, count };
}
export class BatchFinish {
  constructor() { this.serial = 0; this.tickets = new Map(); this.commands = new Map(); this.reset(); }
  reset() { this.frame = { mode: 'Auto', remaining: null, previous: null, version: ++this.serial }; }
  snapshot() {
    const f = this.frame;
    const pending = [...this.tickets.values()].filter(t => !t.done && t.version === f.version).reduce((n,t) => n + t.quantity, 0);
    return { mode: f.mode, remaining: f.remaining, previous: f.previous?.mode || null, pending };
  }
  label() {
    const s = this.snapshot(), name = BATCH_FINISHES.find(f => f.value === s.mode).label;
    return s.remaining === null ? name + ' · until changed' : name + ' · ' + s.remaining + ' copies left → ' + BATCH_FINISHES.find(f => f.value === s.previous).label;
  }
  set(mode, count = null) {
    if (!BATCH_FINISHES.some(f => f.value === mode)) throw new Error('Choose Auto, Normal, Reverse Holo or Holo.');
    if (count !== null && (!Number.isInteger(count) || count < 1 || count > 999)) throw new Error('Choose 1–999 successful copies.');
    this.frame = { mode, remaining: count, previous: count === null ? null : this.frame, version: ++this.serial };
    return this.snapshot();
  }
  command(text, id) {
    if (id && this.commands.has(id)) return this.commands.get(id);
    const parsed = parseBatchFinish(text);
    if (!parsed) return null;
    let result;
    if (parsed.error) result = { handled: true, answer: parsed.error };
    else { this.set(parsed.mode, parsed.count); result = { handled: true, answer: 'Next additions: ' + this.label() + '. Existing cards and saves already started are unchanged.' }; }
    if (id) this.commands.set(id, result);
    return result;
  }
  begin(token, card, body, override = null) {
    if (this.tickets.has(token)) return this.tickets.get(token);
    const f = this.frame, quantity = Number(body.quantity || 1);
    if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Choose a positive whole quantity.');
    const printing = override || (f.mode === 'Auto' ? body.printing : f.mode);
    if (f.mode !== 'Auto' && finishSupport(card, printing) === 'unsupported') throw new Error(printing + ' is unavailable for this card. Review its finish; nothing was added.');
    const available = f.remaining === null ? Infinity : f.remaining - this.snapshot().pending;
    if (quantity > available) throw new Error(available > 0 ? 'Only ' + available + ' copies remain in this batch. Reduce quantity before adding.' : 'Waiting for the last batch save. Retry any failed save before adding another card.');
    const ticket = { token, version: f.version, frame: f, quantity, done: false, printing,
      body: JSON.parse(JSON.stringify({ ...body, printing, ...(f.mode !== 'Auto' ? { batch_finish: true } : {}) })) };
    this.tickets.set(token, ticket);
    return ticket;
  }
  reject(token) { const t = this.tickets.get(token); if (t && !t.done) this.tickets.delete(token); }
  succeed(token, result) {
    const t = this.tickets.get(token);
    if (!t || t.done) return false;
    if (!Array.isArray(result?.ids) || result.ids.length !== t.quantity || result.ids.some(id => !Number.isInteger(id))) throw new Error('Save result is unconfirmed. Retry the same save.');
    t.done = true;
    // Decrement its original frame, including a temporarily suspended frame.
    if (t.frame.remaining !== null) t.frame.remaining = Math.max(0, t.frame.remaining - t.quantity);
    while (this.frame.remaining === 0 && this.frame.previous) this.frame = this.frame.previous;
    return true;
  }
}
